import { getMediumAssetPath } from '@aytm/lib/common/assets'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport'
import { EcommerceBannerPlacement, IEcommerceAnswerSchema, IEcommerceQuestionSchema } from '../../types'
import { formatPriceWithLocale } from '../../utils/formatPriceWithLocale'
import { useBannerImpression } from '../../hooks/useBannerImpression'
import { hiddenClass } from '../../utils/hiddenClass.ts'
import Rating from './Rating'
import style from './BannerAd.module.scss'

interface IBannerAdProps {
  schema: IEcommerceQuestionSchema
  onItemSelect: (index: number, bannerPlacement?: EcommerceBannerPlacement) => void
}

// Sponsored promo strip: a brand image + up to 3 banner-flagged products, shown above the grid.
// Mirrors Amazon's PromoRow role; banner products are pulled OUT of the grid (see Main.tsx).
// 3 matches the editor's Chewy banner limit (see ECQuestionProductCardChewyRightColumn).
const MAX_ITEMS = 3

interface IBannerAdItemProps {
  schema: IEcommerceQuestionSchema
  answer: IEcommerceAnswerSchema
  languageTag: string
  onItemSelect: (index: number, bannerPlacement?: EcommerceBannerPlacement) => void
}

// One card of the strip. It is a component rather than a loop body so that each card can own its
// own impression hook.
const BannerAdItem = ({ schema, answer, languageTag, onItemSelect }: IBannerAdItemProps) => {
  const bannerRef = useBannerImpression<HTMLButtonElement>(answer.id, 'banner')
  const hasDiscount = Boolean(answer.discounted_price) && answer.price > answer.discounted_price

  return (
    <button
      type="button"
      className={`${style.item} ${hiddenClass(answer)}`}
      data-testid="SW-EC-BannerItem"
      ref={bannerRef}
      onClick={() => onItemSelect(answer.index, 'banner')}
    >
      <div className={style.itemImageWrap}>
        {answer.image ? (
          <div className={style.itemImage} style={{ backgroundImage: `url('${getMediumAssetPath(answer.image)}')` }} />
        ) : (
          <div className={style.noImage} />
        )}
      </div>

      <div className={style.itemInfo}>
        <div className={style.itemTitle} dangerouslySetInnerHTML={{ __html: answer.text }} />

        <div className={style.itemRating}>
          <span className={style.itemRatingValue}>{(answer.rating ?? 0).toFixed(1)}</span>
          <Rating rating={answer.rating ?? 0} />
          {answer.reviews ? <span className={style.itemReviews}>{answer.reviews}</span> : null}
        </div>

        <div className={style.itemPriceRow}>
          {hasDiscount ? (
            <>
              <span
                className={`${style.itemPrice} ${style.itemPriceDiscounted}`}
                dangerouslySetInnerHTML={{
                  __html: formatPriceWithLocale(answer.discounted_price, schema.price_mask, languageTag, true, false)
                }}
              />
              <span
                className={style.itemOldPrice}
                dangerouslySetInnerHTML={{
                  __html: formatPriceWithLocale(answer.price, schema.price_mask, languageTag, true, false)
                }}
              />
            </>
          ) : (
            <span
              className={style.itemPrice}
              dangerouslySetInnerHTML={{
                __html: formatPriceWithLocale(answer.price, schema.price_mask, languageTag, true, false)
              }}
            />
          )}
        </div>
      </div>
    </button>
  )
}

const BannerAd = ({ schema, onItemSelect }: IBannerAdProps) => {
  const { languageTag } = useSurveyLocale()
  const viewport = useViewport()

  const bannerProducts = schema.answers
    .filter(answer => answer.banner && !answer.na && !answer.parent_product)
    .slice(0, MAX_ITEMS)

  if (bannerProducts.length === 0) return null

  return (
    <div className={`${style.bannerAd} ${viewport.mobile ? style.mobile : ''}`} data-testid="SW-EC-Banner">
      {schema.ad_image2 && (
        <div
          className={style.brandImage}
          style={{ backgroundImage: `url('${getMediumAssetPath(schema.ad_image2)}')` }}
        />
      )}
      <div className={style.items}>
        {bannerProducts.map(answer => (
          <BannerAdItem
            key={answer.id}
            schema={schema}
            answer={answer}
            languageTag={languageTag}
            onItemSelect={onItemSelect}
          />
        ))}
      </div>
    </div>
  )
}

export default BannerAd
